import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import logNewRelicEvent from '../utils/newRelicApiLogger';
import { useAppContext } from '../context/AppContext';

export const useFetchHeaderFooter = () => {
    const [headerContent, setHeaderContent] = useState('');
    const [footerContent, setFooterContent] = useState('');
    const [cssJsContent, setCssJsContent] = useState([]);
    const [isFoxtelContentReady, setIsFoxtelContentReady] = useState(false);
    const { setIsFoxtelCssAttached } = useAppContext();
    // fetch header and footer from foxtel
    useEffect(() => {
        async function fetchHeadersFooter() {
            const { loadHeaderFooter } = await import('../services/dataResolver/dataResolver');
            const result = await loadHeaderFooter();

            const cssJsItems = [];

            result.forEach(item => {
                if (item?.type === 'headerHtml') {
                    setHeaderContent(item?.content);
                }
                if (item?.type === 'footerHtml') {
                    setFooterContent(item?.content);
                }
                if (item?.type === 'css') {
                    cssJsItems.push({ css: item?.content });
                }
                if (item?.type === 'js') {
                    cssJsItems.push({ js: item?.content });
                }
            });

            setCssJsContent(cssJsItems);
        }
        fetchHeadersFooter();
    }, []);

    // Inject CSS and JS into the DOM
    useEffect(() => {
        if (!cssJsContent || cssJsContent.length === 0) return;

        const injectedElements = [];

        cssJsContent.forEach(item => {
            // Inject CSS
            if (item.css) {
                const styleElement = document.createElement('style');
                styleElement.textContent = item.css;
                styleElement.setAttribute('data-injected', 'true');
                document.head.appendChild(styleElement);
                injectedElements.push(styleElement);
                setIsFoxtelCssAttached(true)
            }

            // Inject JS
            if (item.js) {
                const scriptElement = document.createElement('script');
                scriptElement.textContent = item.js;
                scriptElement.setAttribute('data-injected', 'true');
                document.body.appendChild(scriptElement);
                injectedElements.push(scriptElement);
            }
        });

        setIsFoxtelContentReady(true);

        // Cleanup function to remove injected elements when component unmounts
        return () => {
            injectedElements.forEach(element => {
                element.remove();
            });
        };
    }, [cssJsContent]);

    return { headerContent, footerContent, cssJsContent, isFoxtelContentReady };
};

export function useDevMode() {
    const [isDevMode, setIsDevMode] = useState(false);

    useEffect(() => {
        const value = localStorage.getItem('devMode');
        setIsDevMode(value === 'true');
    }, []);

    return isDevMode;
}

export function useReferrer() {
    const [referrer, setReferrer] = useState('');

    useEffect(() => {
        setReferrer(document.referrer || 'direct');
    }, []);

    return referrer;
}

/**
 * Custom hook to track initial page load and SPA route change performance metrics
 *
 * This hook automatically tracks:
 * - Initial page load with detailed browser timing metrics (DNS, TCP, response, DOM, etc.)
 * - SPA route changes with render timing
 *
 * Usage in App.jsx:
 * ```
 * import usePageLoadTracking from './hooks/usePageLoadTracking';
 *
 * function App() {
 *   usePageLoadTracking();
 *   return <YourApp />;
 * }
 * ```
 */
export default function usePageLoadTracking() {
    const location = useLocation();
    const isInitialLoad = useRef(true);
    const routeStartTime = useRef(null);

    // Track initial page load with Core Web Vitals
    useEffect(() => {
        const trackInitialPageLoad = () => {
            const nav = performance.getEntriesByType('navigation')[0];

            if (!nav) {
                console.warn('Navigation timing data not available');
                return;
            }

            // Helper to safely calculate durations
            const safeDuration = (end, start) =>
                end > 0 && start > 0 ? Math.round(end - start) : null;

            // Prefer loadEventEnd, fall back to domComplete if not ready
            const pageLoadEnd = nav.loadEventEnd > 0 ? nav.loadEventEnd : nav.domComplete;

            if (!pageLoadEnd || pageLoadEnd <= nav.fetchStart) {
                return;
            }

            // Standard Navigation Timing metrics
            const metrics = {
                // Total page load time
                pageLoadTime: Math.round(pageLoadEnd - nav.fetchStart),

                // DNS lookup time
                dnsTime: safeDuration(nav.domainLookupEnd, nav.domainLookupStart),

                // TCP connection time (including TLS if applicable)
                tcpTime: safeDuration(nav.connectEnd, nav.connectStart),

                // TLS negotiation time (if HTTPS)
                tlsTime:
                    nav.secureConnectionStart > 0
                        ? safeDuration(nav.connectEnd, nav.secureConnectionStart)
                        : null,

                // TTFB - Full user-experienced TTFB from navigation start
                ttfb: safeDuration(nav.responseStart, nav.fetchStart),

                // Server processing time (what you were calling TTFB)
                serverTime: safeDuration(nav.responseStart, nav.requestStart),

                // HTML download time
                downloadTime: safeDuration(nav.responseEnd, nav.responseStart),

                // DOM processing time (HTML parsing)
                domProcessingTime: safeDuration(nav.domInteractive, nav.responseEnd),

                // DOM content loaded (when initial HTML is parsed)
                domContentLoadedTime: safeDuration(nav.domContentLoadedEventEnd, nav.fetchStart),

                // Resource loading time (after DOMContentLoaded)
                resourceLoadTime: safeDuration(pageLoadEnd, nav.domContentLoadedEventEnd),

                // REMOVED: initialRenderTime (was duplicate of domProcessingTime)
            };

            // Track Core Web Vitals
            const webVitals = {};

            // Get FCP, LCP, FID, CLS from PerformanceObserver
            if ('PerformanceObserver' in window) {
                try {
                    // First Contentful Paint
                    const fcpEntry = performance.getEntriesByName('first-contentful-paint')[0];
                    if (fcpEntry) {
                        webVitals.fcp = Math.round(fcpEntry.startTime);
                    }

                    // Largest Contentful Paint
                    new PerformanceObserver(list => {
                        const entries = list.getEntries();
                        const lastEntry = entries[entries.length - 1];
                        webVitals.lcp = Math.round(lastEntry.startTime);
                    }).observe({ type: 'largest-contentful-paint', buffered: true });

                    // Cumulative Layout Shift
                    let clsValue = 0;
                    new PerformanceObserver(list => {
                        for (const entry of list.getEntries()) {
                            if (!entry.hadRecentInput) {
                                clsValue += entry.value;
                            }
                        }
                        webVitals.cls = Math.round(clsValue * 1000) / 1000; // Round to 3 decimals
                    }).observe({ type: 'layout-shift', buffered: true });

                    // First Input Delay (only if user interacts)
                    new PerformanceObserver(list => {
                        const firstInput = list.getEntries()[0];
                        if (firstInput) {
                            webVitals.fid = Math.round(
                                firstInput.processingStart - firstInput.startTime,
                            );
                        }
                    }).observe({ type: 'first-input', buffered: true });
                } catch (error) {
                    console.warn('Error capturing Web Vitals:', error);
                }
            }

            // Log after a delay to capture CLS/LCP (they update over time)
            setTimeout(() => {
                console.log('🚀 Page load time:');
                console.dir({
                    performanceType: 'initial_page_load',
                    ...metrics,
                    ...webVitals,
                    pathname: location.pathname,
                    timestamp: new Date().toISOString(),

                    // Add useful context
                    navigationTiming: nav.type, // 0=navigate, 1=reload, 2=back/forward
                    transferSize: nav.transferSize || null, // Bytes transferred

                    // Connection info
                    connectionType: navigator.connection?.effectiveType || 'unknown',
                    downlink: navigator.connection?.downlink || null,
                });
                logNewRelicEvent({
                    eventName: 'page_performance',
                    attributes: {
                        performanceType: 'initial_page_load',
                        ...metrics,
                        ...webVitals,
                        pathname: location.pathname,
                        timestamp: new Date().toISOString(),

                        // Add useful context
                        navigationTiming: nav.type, // 0=navigate, 1=reload, 2=back/forward
                        transferSize: nav.transferSize || null, // Bytes transferred

                        // Connection info
                        connectionType: navigator.connection?.effectiveType || 'unknown',
                        downlink: navigator.connection?.downlink || null,
                    },
                });
            }, 3000); // Wait 3s to capture stable LCP/CLS
        };

        if (isInitialLoad.current) {
            if (document.readyState === 'complete') {
                trackInitialPageLoad();
            } else {
                window.addEventListener('load', trackInitialPageLoad);
                return () => window.removeEventListener('load', trackInitialPageLoad);
            }

            isInitialLoad.current = false;
        }
    }, []);

    // Track SPA route changes (this part is good!)
    useEffect(() => {
        if (isInitialLoad.current) {
            return;
        }

        routeStartTime.current = performance.now();

        const trackRouteChange = () => {
            if (routeStartTime.current === null) return;

            const routeLoadTime = Math.round(performance.now() - routeStartTime.current);

            logNewRelicEvent({
                eventName: 'page_performance',
                attributes: {
                    performanceType: 'spa_route_change',
                    routeLoadTime,
                    pathname: location.pathname,
                    previousPathname: sessionStorage.getItem('previousPath') || 'unknown',
                    timestamp: new Date().toISOString(),
                },
            });

            if (window.newrelic?.interaction) {
                window.newrelic.interaction().setName(`Route: ${location.pathname}`).save();
            }

            console.log(`⚡ SPA Route Change to ${location.pathname}: ${routeLoadTime}ms`);
            sessionStorage.setItem('previousPath', location.pathname);
        };

        if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
            const idleCallbackId = window.requestIdleCallback(trackRouteChange, { timeout: 2000 });
            return () => {
                if (typeof window.cancelIdleCallback === 'function') {
                    window.cancelIdleCallback(idleCallbackId);
                }
            };
        } else {
            const timeoutId = setTimeout(trackRouteChange, 0);
            return () => clearTimeout(timeoutId);
        }
    }, [location.pathname]);

    // Session end tracking (this is fine)
    useEffect(() => {
        const handleBeforeUnload = () => {
            const sessionDuration = performance.now();

            logNewRelicEvent({
                eventName: 'page_performance',
                attributes: {
                    performanceType: 'session_end',
                    sessionDuration: Math.round(sessionDuration),
                    pathname: location.pathname,
                    timestamp: new Date().toISOString(),
                },
            });
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [location.pathname]);
}
